import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Mic, MicOff, Square, Send, X, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/context/LanguageContext';
import { sttApi } from '@/services/api';

export default function VoiceInput({ 
  onTranscript, 
  onStateChange, 
  disabled = false,
  className = '',
  showInline = true,
  inputComponent = null
}) {
  const { language } = useLanguage();
  
  // Translations (memoized)
  const translations = useMemo(() => ({
    recording: language === 'en' ? 'Recording...' : 'Enregistrement...',
    tapToStop: language === 'en' ? 'Tap to stop' : 'Appuyez pour arrêter',
    micError: language === 'en' ? 'Microphone Error' : 'Erreur Microphone',
    micDenied: language === 'en' ? 'Microphone access denied. Please allow access in your browser settings.' : 'Accès au microphone refusé. Veuillez autoriser l\'accès dans les paramètres de votre navigateur.',
    micNotFound: language === 'en' ? 'No microphone found' : 'Aucun microphone trouvé',
    retry: language === 'en' ? 'Retry' : 'Réessayer',
    cancel: language === 'en' ? 'Cancel' : 'Annuler',
    send: language === 'en' ? 'Send' : 'Envoyer',
    listening: language === 'en' ? 'Listening...' : 'Écoute en cours...',
    preview: language === 'en' ? 'Preview' : 'Aperçu',
    continueRec: language === 'en' ? 'Continue' : 'Continuer',
    notSupported: language === 'en' ? 'Voice not supported' : 'Voix non supportée',
    noText: language === 'en' ? '(No text captured)' : '(Aucun texte capturé)',
    transcribing: language === 'en' ? 'Transcribing with AI...' : 'Transcription IA en cours...',
    transcriptionError: language === 'en' ? 'Transcription failed. Please try again.' : 'Échec de la transcription. Veuillez réessayer.'
  }), [language]);

  // States: idle, recording, transcribing, preview, error
  const [state, setState] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [transcript, setTranscript] = useState('');
  const [duration, setDuration] = useState(0);
  
  // Refs
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);

  // Stop media stream helper
  const stopMediaStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  }, []);

  // Notify parent of active state
  useEffect(() => {
    const isActive = state !== 'idle';
    if (onStateChange) {
      onStateChange(isActive);
    }
  }, [state, onStateChange]);

  // Timer effect
  useEffect(() => {
    if (state === 'recording') {
      timerRef.current = setInterval(() => {
        setDuration(d => d + 1);
      }, 1000);
    } else if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [state]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopMediaStream();
    };
  }, [stopMediaStream]);

  const formatTime = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const startRecording = useCallback(async () => {
    setErrorMsg('');
    setTranscript('');
    setDuration(0);
    audioChunksRef.current = [];

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 16000 // Optimal for speech recognition
        } 
      });
      streamRef.current = stream;

      // Use webm for best compatibility with Whisper
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') 
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm') 
          ? 'audio/webm' 
          : 'audio/mp4';

      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };

      mediaRecorder.start(1000); // Collect data every second
      setState('recording');
    } catch (err) {
      console.error('Microphone error:', err);
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setErrorMsg(translations.micDenied);
      } else if (err.name === 'NotFoundError') {
        setErrorMsg(translations.micNotFound);
      } else {
        setErrorMsg(err.message || translations.micError);
      }
      setState('error');
    }
  }, [translations]);

  const stopRecording = useCallback(async () => {
    // Stop recording
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    stopMediaStream();

    // Wait a bit for final chunks
    await new Promise(resolve => setTimeout(resolve, 500));

    // Create audio blob
    if (audioChunksRef.current.length === 0) {
      setErrorMsg(translations.noText);
      setState('error');
      return;
    }

    const mimeType = mediaRecorderRef.current?.mimeType || 'audio/webm';
    const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
    
    // Check if audio is too short (less than 0.5 seconds of data)
    if (audioBlob.size < 5000) {
      setErrorMsg(language === 'en' ? 'Recording too short. Please try again.' : 'Enregistrement trop court. Veuillez réessayer.');
      setState('error');
      return;
    }

    // Transcribe with Whisper
    setState('transcribing');
    
    try {
      const langCode = language === 'en' ? 'en' : 'fr';
      const response = await sttApi.transcribe(audioBlob, langCode);
      
      if (response.data.success && response.data.text) {
        setTranscript(response.data.text);
        setState('preview');
      } else {
        setErrorMsg(response.data.error || translations.transcriptionError);
        setState('error');
      }
    } catch (err) {
      console.error('Transcription error:', err);
      setErrorMsg(translations.transcriptionError);
      setState('error');
    }
  }, [stopMediaStream, translations, language]);

  const cancelRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try { 
        mediaRecorderRef.current.stop(); 
      } catch(e) { 
        console.log('Cancel recorder:', e); 
      }
    }
    stopMediaStream();
    setState('idle');
    setTranscript('');
    setDuration(0);
    audioChunksRef.current = [];
  }, [stopMediaStream]);

  const sendMessage = useCallback(() => {
    if (transcript.trim() && onTranscript) {
      onTranscript(transcript.trim());
    }
    setState('idle');
    setTranscript('');
    setDuration(0);
  }, [transcript, onTranscript]);

  // Check support
  const isSupported = typeof navigator !== 'undefined' && 
    navigator.mediaDevices && 
    typeof navigator.mediaDevices.getUserMedia === 'function';

  // Mic button component (reused in multiple places)
  const MicButton = () => (
    <Button
      variant="outline"
      size="icon"
      onClick={startRecording}
      disabled={disabled || !isSupported}
      className={cn(
        "rounded-full w-10 h-10 sm:w-12 sm:h-12 shrink-0",
        "border-[#5BA946] text-[#5BA946]",
        "hover:bg-[#5BA946] hover:text-white",
        "active:scale-95 transition-all duration-200",
        !isSupported && "opacity-50 cursor-not-allowed",
        className
      )}
      data-testid="voice-btn"
      title={isSupported ? (language === 'en' ? 'Voice message' : 'Message vocal') : translations.notSupported}
    >
      {isSupported ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
    </Button>
  );

  // ERROR STATE
  if (state === 'error') {
    return (
      <div 
        className="w-full bg-white rounded-2xl shadow-xl border border-red-100 overflow-hidden"
        data-testid="voice-error"
      >
        <div className="bg-gradient-to-r from-red-500 to-red-600 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-white">
            <MicOff className="w-5 h-5" />
            <span className="font-semibold">{translations.micError}</span>
          </div>
          <button 
            onClick={cancelRecording}
            className="text-white/80 hover:text-white p-1 rounded-full hover:bg-white/10"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-5">
          <p className="text-gray-600 text-sm leading-relaxed mb-5">
            {errorMsg}
          </p>
          
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={cancelRecording}
              className="flex-1 h-11 rounded-xl"
            >
              {translations.cancel}
            </Button>
            <Button
              onClick={startRecording}
              className="flex-1 h-11 rounded-xl bg-red-500 hover:bg-red-600 text-white"
            >
              {translations.retry}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // TRANSCRIBING STATE - AI processing
  if (state === 'transcribing') {
    return (
      <div 
        className="w-full bg-white rounded-2xl shadow-xl border border-blue-200 overflow-hidden"
        data-testid="voice-transcribing"
      >
        <div className="bg-gradient-to-r from-blue-500 to-indigo-600 px-4 py-3">
          <div className="flex items-center gap-3 text-white">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="font-semibold">{translations.transcribing}</span>
          </div>
        </div>
        
        <div className="p-6 flex flex-col items-center justify-center">
          <div className="w-16 h-16 mb-4 rounded-full bg-blue-100 flex items-center justify-center">
            <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
          </div>
          <p className="text-gray-500 text-sm text-center">
            {language === 'en' 
              ? 'Using OpenAI Whisper for accurate transcription...' 
              : 'Utilisation de OpenAI Whisper pour une transcription précise...'}
          </p>
        </div>
      </div>
    );
  }

  // RECORDING STATE
  if (state === 'recording') {
    return (
      <div 
        className="w-full bg-white rounded-2xl shadow-xl border-2 border-red-300 overflow-hidden"
        data-testid="voice-recording"
      >
        <div className="bg-gradient-to-r from-red-500 to-red-600 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="relative flex items-center justify-center">
              <div className="w-3 h-3 bg-white rounded-full animate-pulse" />
              <div className="absolute w-3 h-3 bg-white rounded-full animate-ping" />
            </div>
            
            <div className="flex items-end gap-[2px] h-4">
              {[1,2,3,4,5].map(i => (
                <div 
                  key={i}
                  className="w-1 bg-white rounded-full animate-soundwave"
                  style={{ animationDelay: `${i * 0.1}s` }}
                />
              ))}
            </div>
            
            <span className="text-white font-medium">{translations.recording}</span>
          </div>
          
          <div className="flex items-center gap-3">
            <span className="text-white/90 font-mono text-sm bg-white/20 px-2 py-0.5 rounded">
              {formatTime(duration)}
            </span>
            <button 
              onClick={cancelRecording}
              className="text-white/80 hover:text-white p-1 rounded-full hover:bg-white/10"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        <div className="p-4">
          <div className="bg-red-50 rounded-xl p-4 min-h-[60px] flex items-center justify-center mb-4">
            <p className="text-gray-500 text-sm italic flex items-center gap-2">
              <Mic className="w-4 h-4 animate-pulse text-red-500" />
              {translations.listening}
            </p>
          </div>
          
          <Button
            onClick={stopRecording}
            className="w-full h-14 rounded-xl bg-red-500 hover:bg-red-600 text-white text-base font-semibold shadow-lg"
            data-testid="voice-stop-btn"
          >
            <Square className="w-5 h-5 mr-2 fill-current" />
            {translations.tapToStop}
          </Button>
        </div>
      </div>
    );
  }

  // PREVIEW STATE
  if (state === 'preview') {
    return (
      <div 
        className="w-full bg-white rounded-2xl shadow-xl border border-green-200 overflow-hidden"
        data-testid="voice-preview"
      >
        <div className="bg-gradient-to-r from-green-500 to-emerald-600 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-white">
            <Mic className="w-5 h-5" />
            <span className="font-semibold">{translations.preview}</span>
          </div>
          <button 
            onClick={cancelRecording}
            className="text-white/80 hover:text-white p-1 rounded-full hover:bg-white/10"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-4">
          <div className="bg-green-50 rounded-xl p-4 min-h-[80px] max-h-[120px] overflow-y-auto mb-4">
            {transcript ? (
              <p className="text-gray-800 leading-relaxed">{transcript}</p>
            ) : (
              <p className="text-gray-400 italic">{translations.noText}</p>
            )}
          </div>
          
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={startRecording}
              className="flex-1 h-11 rounded-xl border-green-200 text-green-600 hover:bg-green-50"
            >
              <Mic className="w-4 h-4 mr-2" />
              {translations.continueRec}
            </Button>
            <Button
              onClick={sendMessage}
              disabled={!transcript.trim()}
              className="flex-1 h-11 rounded-xl bg-[#5BA946] hover:bg-[#4a9339] text-white disabled:opacity-50"
              data-testid="voice-send-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              {translations.send}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // IDLE STATE - Show inline with input component if provided
  if (showInline && inputComponent) {
    return (
      <div className="flex items-center gap-1 sm:gap-2 w-full">
        {inputComponent}
        <MicButton />
      </div>
    );
  }

  // Just the mic button
  return <MicButton />;
}
